import React from "react";

import {
  mdiCalculatorVariantOutline,
  mdiChevronDown,
  mdiClockOutline,
  mdiDownload,
  mdiFilmstrip,
  mdiLightningBolt,
  mdiMicrophone,
  mdiTelevisionBox,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";

import EncodingWarningModal from "./EncodingWarningModal";
import FormatBadge from "./FormatBadge";
import { SectionTitle, SubSectionRow } from "./PanelSection";
import useEncodingPanel from "../../hooks/useEncodingPanel";
import prettyDurations, { DURATION_UNITS } from "../../utils/durations";
import Block from "../block/Block";
import Button from "../button/Button";

import type { FastAsset } from "@/src/api/MediaAPI.types";

import type {
  EncodingProfile,
  PivotMedia,
  SourceMedia,
} from "../../api/EncoderAPI.types";
import type { StartEncodingOutcome } from "../../hooks/useMediaEncoding";
import type { ComputedQuotas } from "../../utils/quotas";

interface EncodingConfigPanelProps {
  sourceMedias: (SourceMedia | PivotMedia)[];
  encodingProfiles: EncodingProfile[];
  isProfilesLoading: boolean;
  isEncoding: boolean;
  isStartingEncoding: boolean;
  fastAsset: FastAsset | null | undefined;
  onStartEncoding: (profileUuid: string) => Promise<StartEncodingOutcome>;
}

interface CostInfoBarProps {
  sourceMedias: (SourceMedia | PivotMedia)[];
  costFactor: number;
  quotas: ComputedQuotas | null;
  withLabels?: boolean;
}

export function CostInfoBar({
  sourceMedias,
  costFactor,
  quotas,
  withLabels,
}: CostInfoBarProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const videoSource = sourceMedias
    .flatMap((src) => src.info?.streams ?? [])
    .find((s) => s.codec_type === "video");

  const durationSeconds = videoSource?.duration
    ? parseFloat(videoSource.duration)
    : null;

  const videoDuration =
    durationSeconds !== null
      ? prettyDurations(durationSeconds * 1000, {
          valueUnit: DURATION_UNITS.MILLISECONDS,
          format: { noDays: true },
        }).humanised
      : null;

  const estimatedCostMs =
    durationSeconds !== null ? durationSeconds * costFactor * 1000 : null;

  const encodingCost =
    estimatedCostMs !== null
      ? prettyDurations(estimatedCostMs, {
          valueUnit: DURATION_UNITS.MILLISECONDS,
          format: { noDays: true },
        }).humanised
      : null;

  const availableDuration =
    quotas !== null
      ? prettyDurations(quotas.available.seconds * 1000, {
          valueUnit: DURATION_UNITS.MILLISECONDS,
          format: { noDays: true },
        }).humanised
      : null;

  return (
    <div className="border-border flex flex-wrap items-center justify-between gap-6 rounded-md border p-2 shadow-[inset_0_0_5px_rgba(0,0,0,0.05)]">
      <div className="text-muted-foreground flex items-center gap-[0.78125rem] text-[0.78125rem]">
        {videoDuration !== null && (
          <React.Fragment>
            <span className="inline-flex items-center gap-2">
              <Icon path={mdiClockOutline} size={1} />
              {withLabels ? <span>{t("mediaDuration")}</span> : null}
              {videoDuration}
            </span>
            <span className="text-border">|</span>
          </React.Fragment>
        )}
        <span className="inline-flex items-center gap-2">
          <Icon path={mdiCalculatorVariantOutline} size={1} />
          {withLabels ? <span>{t("costMultiplier")}</span> : null}
          <span>x{costFactor}</span>
        </span>
      </div>
      <div className="flex flex-1 flex-wrap justify-end gap-2">
        {encodingCost !== null && (
          <span className="border-primary/40 bg-primary/5 text-muted-foreground flex flex-wrap gap-2 rounded-md border px-2 py-1 text-xs font-medium tracking-wide">
            <span className="whitespace-nowrap uppercase">
              {t("estimatedCost")}{" "}
            </span>
            <span className="text-primary font-semibold whitespace-nowrap">
              {encodingCost}
            </span>
          </span>
        )}
        {availableDuration !== null && (
          <span className="border-border bg-muted text-muted-foreground flex flex-wrap gap-2 rounded-md border px-2 py-1 text-xs font-medium tracking-wide">
            <span className="whitespace-nowrap uppercase">
              {t("availableQuota")}
            </span>
            <span className="text-muted-foreground font-semibold">
              {availableDuration}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

export default function EncodingConfigPanel({
  sourceMedias,
  encodingProfiles,
  isProfilesLoading,
  isEncoding,
  isStartingEncoding,
  onStartEncoding,
  fastAsset,
}: EncodingConfigPanelProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const {
    selectedProfileUuid,
    selectedProfile,
    setManualSelection,
    allQualitiesEncoded,
    resetAllQualitiesEncoded,
    canStartEncoding,
    startEncodingTooltip,
    quotas,
    videoFormatList,
    audioFormatList,
    isVideoFormatLocked,
    isAudioFormatLocked,
    videoFormatLockedTooltip,
    audioFormatLockedTooltip,
    isFastOptionLocked,
    isD2gOptionLocked,
    handleEncodingClick,
    isOpenEncodingWarningModal,
    closeEncodingWarningModal,
    confirmEncoding,
    isFramerateMismatch,
  } = useEncodingPanel({
    sourceMedias,
    encodingProfiles,
    isEncoding,
    isStartingEncoding,
    onStartEncoding,
    fastAsset,
  });

  const hasOptions =
    selectedProfile?.fast || selectedProfile?.ott_download_to_go;

  const headerComponent = (
    <Button
      withGlow
      data-tooltip-content={startEncodingTooltip}
      data-tooltip-hidden={canStartEncoding ? !allQualitiesEncoded : undefined}
      data-tooltip-id="start-encoding-tooltip"
      isDisabled={!canStartEncoding}
      onClick={handleEncodingClick}
      size="xs"
      type="button"
    >
      <Icon path={mdiLightningBolt} size={0.625} />
      {isEncoding ? t("encodingInProgress") : t("startEncoding")}
    </Button>
  );

  const getFastTooltip = (): string | undefined => {
    if (isFastOptionLocked) return t("fastLocked");

    if (isFramerateMismatch) {
      return t("fastFramerateMismatch");
    }

    return undefined;
  };

  const getFastStatus = (): "neutral" | "warning" | "unavailable" => {
    if (isFastOptionLocked || !selectedProfile?.fast) {
      return "unavailable";
    }

    if (isFramerateMismatch) {
      return "warning";
    }

    return "neutral";
  };

  return (
    <React.Fragment>
      <Block
        isFullSize
        classNameHeader="h-[4.375rem]"
        headerComponent={headerComponent}
        title={t("encodingConfigPanelTitle")}
      >
        <div className="flex h-full flex-col gap-6">
          <div>
            {isProfilesLoading ? (
              <p className="text-muted-foreground text-2xl">
                {t("loadingProfiles")}
              </p>
            ) : (
              <div className="relative">
                <select
                  className="focus:border-primary border-border bg-card text-foreground w-full appearance-none rounded-md border py-1.5 pr-6.25 pl-2.5 text-base focus:outline-none"
                  id="encoding-profile-select"
                  value={selectedProfileUuid ?? ""}
                  onChange={(e): void => {
                    setManualSelection(e.target.value);
                    resetAllQualitiesEncoded();
                  }}
                >
                  {encodingProfiles.map((profile) => (
                    <option key={profile.uuid} value={profile.uuid}>
                      {profile.label}
                    </option>
                  ))}
                </select>
                <Icon
                  className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2"
                  path={mdiChevronDown}
                  size={1}
                />
              </div>
            )}
          </div>

          {selectedProfile ? (
            <CostInfoBar
              costFactor={selectedProfile.cost_factor}
              quotas={quotas}
              sourceMedias={sourceMedias}
            />
          ) : null}

          {selectedProfile ? (
            <React.Fragment>
              {(videoFormatList.length > 0 || audioFormatList.length > 0) && (
                <div>
                  <SectionTitle label={t("formats")} />
                  <div className="flex flex-col gap-[0.78125rem]">
                    {videoFormatList.length > 0 && (
                      <SubSectionRow icon={mdiFilmstrip} label={t("video")}>
                        {videoFormatList.map((label) => (
                          <FormatBadge
                            key={label}
                            label={label}
                            tooltipContent={videoFormatLockedTooltip(label)}
                            status={
                              isVideoFormatLocked(label)
                                ? "unavailable"
                                : "neutral"
                            }
                          />
                        ))}
                      </SubSectionRow>
                    )}

                    {audioFormatList.length > 0 && (
                      <SubSectionRow icon={mdiMicrophone} label={t("audio")}>
                        {audioFormatList.map((label) => (
                          <FormatBadge
                            key={label}
                            label={label}
                            tooltipContent={audioFormatLockedTooltip(label)}
                            status={
                              isAudioFormatLocked(label)
                                ? "unavailable"
                                : "neutral"
                            }
                          />
                        ))}
                      </SubSectionRow>
                    )}
                  </div>
                </div>
              )}

              {hasOptions ? (
                <div>
                  <SectionTitle label={t("optionsFormats")} />
                  <div className="flex flex-col gap-[0.78125rem]">
                    {selectedProfile.ott_download_to_go ? (
                      <SubSectionRow icon={mdiDownload} label={t("d2gShort")}>
                        <FormatBadge
                          label={t("d2g")}
                          status={isD2gOptionLocked ? "unavailable" : "neutral"}
                          tooltipContent={
                            isD2gOptionLocked ? t("d2gLocked") : undefined
                          }
                        />
                      </SubSectionRow>
                    ) : null}
                    {selectedProfile.fast ? (
                      <SubSectionRow icon={mdiTelevisionBox} label={t("fast")}>
                        <FormatBadge
                          label={`${t("fast")} ${selectedProfile.fast_framerate_target} fps`}
                          status={getFastStatus()}
                          tooltipContent={getFastTooltip()}
                        />
                      </SubSectionRow>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </React.Fragment>
          ) : null}
        </div>
      </Block>
      <EncodingWarningModal
        isOpen={isOpenEncodingWarningModal}
        onClose={closeEncodingWarningModal}
        onConfirm={confirmEncoding}
      />
    </React.Fragment>
  );
}
